// src/context/ExportContext.jsx
// Global export state so the progress dialogs / floating bar can live outside
// BoardPack (at the App level) and survive BoardPack navigating away mid-export.
import { createContext, useCallback, useContext, useRef, useState } from "react";

const ExportContext = createContext(null);

export const ExportProvider = ({ children }) => {
  const [isExporting, setIsExporting] = useState(false);

  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfMinimized, setPdfMinimized] = useState(false);

  const [pptDialogOpen, setPptDialogOpen] = useState(false);
  const [pptProgress, setPptProgress] = useState(0);
  const [pptMinimized, setPptMinimized] = useState(false);

  const exportCancelledRef = useRef(false);

  // The DOM node BoardPackPage exposes while the /board-pack route is active —
  // BoardPackExportHost portals BoardPack's content into it. Null when the
  // route isn't mounted (BoardPackExportHost then falls back to an off-screen
  // holder for as long as an export keeps BoardPack alive in the background).
  const [visibleSlotEl, setVisibleSlotEl] = useState(null);
  const registerVisibleSlot = useCallback((el) => setVisibleSlotEl(el), []);

  const beginExport = useCallback((type) => {
    exportCancelledRef.current = false;
    if (type === "pdf") {
      setPdfDialogOpen(true);
      setPdfProgress(0);
      setPdfMinimized(false);
    } else {
      setPptDialogOpen(true);
      setPptProgress(0);
      setPptMinimized(false);
    }
    setIsExporting(true);
  }, []);

  const finishExport = useCallback((type) => {
    setIsExporting(false);
    if (type === "pdf") {
      setPdfDialogOpen(false);
      setPdfProgress(0);
      setPdfMinimized(false);
    } else {
      setPptDialogOpen(false);
      setPptProgress(0);
      setPptMinimized(false);
    }
  }, []);

  const cancelExport = useCallback(
    (type) => {
      exportCancelledRef.current = true;
      finishExport(type);
    },
    [finishExport]
  );

  const minimizeExport = useCallback((type) => {
    if (type === "pdf") setPdfMinimized(true);
    else setPptMinimized(true);
  }, []);

  const restoreExport = useCallback((type) => {
    if (type === "pdf") setPdfMinimized(false);
    else setPptMinimized(false);
  }, []);

  const value = {
    isExporting,
    exportCancelledRef,

    pdfDialogOpen,
    pdfProgress,
    setPdfProgress,
    pdfMinimized,

    pptDialogOpen,
    pptProgress,
    setPptProgress,
    pptMinimized,

    beginExport,
    finishExport,
    cancelExport,
    minimizeExport,
    restoreExport,

    visibleSlotEl,
    registerVisibleSlot,
  };

  return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>;
};

export const useExport = () => {
  const ctx = useContext(ExportContext);
  if (!ctx) throw new Error("useExport must be used within an ExportProvider");
  return ctx;
};
